/**
 * The verb sentence a card leads with.
 *
 * VERB FIRST, TOOL NAME SECOND. An operator glancing at the corner of the
 * screen needs to know what was done to their client's data, and
 * `planner_schedule_wp_publish` answers that only for someone who already
 * knows the catalog. The registered name still appears, underneath, because it
 * is what an engineer needs to reproduce the call.
 *
 * Derived from the tool name rather than from `Command.description`. The
 * description is written for a model, runs to several sentences, and names
 * refusal conditions and argument shapes that would swamp a card.
 */
import type { AnyCommand, Consequence } from '../contract';
import { CATALOG } from '../catalog';

/**
 * Words whose sentence-case rendering is wrong. Everything else is lowercased
 * and the sentence gets one capital at the front.
 */
const SPELLINGS: Readonly<Record<string, string>> = {
  wp: 'WordPress',
  bb: 'Brand Bible',
  url: 'URL',
  urls: 'URLs',
  id: 'ID',
  ids: 'IDs',
  ui: 'UI',
  seo: 'SEO',
  cta: 'CTA',
  faq: 'FAQ',
  api: 'API',
  html: 'HTML',
  json: 'JSON',
};

/** The surface a prefix names, for the card's second line. */
const AREAS: Readonly<Record<string, string>> = {
  planner: 'Content planner',
  bb: 'Brand Bible',
};

export function areaOf(toolName: string): string {
  const prefix = toolName.split('_')[0] ?? '';
  return AREAS[prefix] ?? prefix;
}

/**
 * `planner_schedule_wp_publish` becomes `Schedule WordPress publish`.
 *
 * The area prefix is dropped rather than spoken, because it is the subject and
 * not the verb, and the card already carries the area on its own line.
 */
export function verbSentence(toolName: string): string {
  const parts = toolName.split(/[_.-]+/).filter((part) => part.length > 0);
  const body = parts.length > 1 && AREAS[parts[0] ?? ''] !== undefined ? parts.slice(1) : parts;
  if (body.length === 0) return toolName;

  const words = body.map((word) => SPELLINGS[word.toLowerCase()] ?? word.toLowerCase());
  const first = words[0] ?? '';
  const head = SPELLINGS[body[0]?.toLowerCase() ?? ''] ?? first.charAt(0).toUpperCase() + first.slice(1);
  return [head, ...words.slice(1)].join(' ');
}

/** The catalog entry backing a registered tool, when this build holds one. */
export function commandFor(toolName: string): AnyCommand | null {
  const commands = CATALOG.catalog as Readonly<Record<string, AnyCommand | undefined>>;
  return commands[toolName] ?? null;
}

/** The declared consequence, or null when the tool is not ours. */
export function consequenceFor(toolName: string): Consequence | null {
  return commandFor(toolName)?.consequence ?? null;
}
